/**
 * The stasher's connected AI apps (plans/mcp-oauth.md Phase 8; REQ-28,
 * REQ-29): the MCP clients they approved, and revoking one. Relative paths
 * through the dev proxy, Firebase ID token as Bearer, ApiResponse envelope.
 * Failures are RETURNED, never thrown.
 */
import axios from 'axios';
import type { OAuthConnection } from '@stash/shared-types';
import { auth } from './firebase';

const BASE_URL = '/api/v1/stashers/me/connections';

export type ConnectionsServiceError =
  | { kind: 'auth' }
  | { kind: 'notFound' }
  | { kind: 'serverError' }
  | { kind: 'network' };

export type ConnectionsResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: ConnectionsServiceError };

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

function mapError(error: unknown): ConnectionsServiceError {
  const status = (error as { response?: { status?: number } }).response?.status;
  switch (status) {
    case 401:
    case 403: return { kind: 'auth' };
    case 404: return { kind: 'notFound' };
    case undefined: return { kind: 'network' };
    default: return { kind: 'serverError' };
  }
}

export class ConnectionsService {
  private async authHeaders(): Promise<Record<string, string> | null> {
    // On a fresh page load Firebase restores the session asynchronously;
    // currentUser is null until it has.
    await auth.authStateReady();
    const user = auth.currentUser;
    if (!user) return null;
    return { Authorization: `Bearer ${await user.getIdToken()}` };
  }

  async list(): Promise<ConnectionsResult<OAuthConnection[]>> {
    const headers = await this.authHeaders();
    if (!headers) return { ok: false, error: { kind: 'auth' } };
    try {
      const response = await axios.get<ApiResponse<OAuthConnection[]>>(BASE_URL, { headers });
      return { ok: true, data: response.data.data };
    } catch (error) {
      return { ok: false, error: mapError(error) };
    }
  }

  async revoke(id: string): Promise<ConnectionsResult<null>> {
    const headers = await this.authHeaders();
    if (!headers) return { ok: false, error: { kind: 'auth' } };
    try {
      await axios.delete<ApiResponse<null>>(`${BASE_URL}/${encodeURIComponent(id)}`, { headers });
      return { ok: true, data: null };
    } catch (error) {
      return { ok: false, error: mapError(error) };
    }
  }
}

export const connectionsService = new ConnectionsService();
